import { motion, AnimatePresence } from "motion/react";
import {
  BookOpen,
  GraduationCap,
  Lightbulb,
  Users,
  FileText,
  Award,
  Brain,
  Target,
  Rocket,
  Star,
} from "lucide-react";
import { Box } from "@mui/material";
import svgPaths from "../Loading/svg-hkmuvmlym6";

interface AnimatedLogoWithIconsProps {
  progress: number;
  currentCycle: number;
  totalCycles: number;
  size?: number; 
}

const educationIcons = [
  { Icon: BookOpen, label: "Aprendizaje" },
  { Icon: GraduationCap, label: "Educación" },
  { Icon: Lightbulb, label: "Ideas" },
  { Icon: Brain, label: "Pensamiento" },
  { Icon: Users, label: "Colaboración" },
  { Icon: FileText, label: "Proyectos" },
  { Icon: Target, label: "Objetivos" },
  { Icon: Rocket, label: "Innovación" },
  { Icon: Award, label: "Logros" },
  { Icon: Star, label: "Excelencia" },
];

export default function AnimatedLogoWithIcons({
  progress,
  currentCycle,
  totalCycles,
  size = 280,
}: AnimatedLogoWithIconsProps) {
  const cycleProgress = progress;
  const isShowingIcon = cycleProgress >= 95;

  const currentIcon = educationIcons[currentCycle % educationIcons.length];
  const { Icon } = currentIcon;

  const topLineProgress = Math.min(cycleProgress * 2, 100);
  const bottomLineProgress = Math.max((cycleProgress - 50) * 2, 0);

  return (
    <Box
      sx={{
        position: "relative",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AnimatePresence mode="wait">
        {!isShowingIcon ? (
          <motion.div
            key={`logo-${currentCycle}`}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              width={224}
              height={208}
              fill="none"
              preserveAspectRatio="xMidYMid meet"
              viewBox="0 0 224 208"
            >
              <defs>
                {/* top line clip */}
                <clipPath id="topLineFill">
                  <motion.rect
                    x="0"
                    y="0"
                    height="104"
                    initial={{ width: 0 }}
                    animate={{ width: (224 * topLineProgress) / 100 }}
                    transition={{ duration: 0.05, ease: "linear" }}
                  />
                </clipPath>

                {/* bottom line clip */}
                <clipPath id="bottomLineFill">
                  <motion.rect
                    y="104"
                    height="104"
                    initial={{ x: 224, width: 0 }}
                    animate={{
                      x: 224 - (224 * bottomLineProgress) / 100,
                      width: (224 * bottomLineProgress) / 100,
                    }}
                    transition={{ duration: 0.05, ease: "linear" }}
                  />
                </clipPath>
              </defs>

              {/* base gris */}
              <g>
                <path d={svgPaths.p39165200} fill="#D1D5DB" />
                <path d={svgPaths.p21459100} fill="#D1D5DB" />
                <path d={svgPaths.p1843e680} fill="#D1D5DB" />
              </g>

              {/* top fill */}
              <g clipPath="url(#topLineFill)">
                <path d={svgPaths.p21459100} fill="#336B87" />
                <path d={svgPaths.p39165200} fill="#336B87" />
              </g>

              {/* bottom fill */}
              <g clipPath="url(#bottomLineFill)">
                <path d={svgPaths.p1843e680} fill="#336B87" />
              </g>
            </svg>
          </motion.div>
        ) : (
          <motion.div
            key={`icon-${currentCycle}`}
            initial={{ scale: 0, rotate: -120, opacity: 0 }}
            animate={{ scale: 1, rotate: 0, opacity: 1 }}
            exit={{ scale: 0, rotate: 120, opacity: 0 }}
            transition={{
              duration: 0.5,
              type: "spring",
              stiffness: 200,
              damping: 15,
            }}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              style={{
                borderRadius: 999,
                padding: 64,
                background: "#336B87",
              }}
              animate={{
                boxShadow: [
                  "0 10px 60px rgba(51, 107, 135, 0.3)",
                  "0 10px 100px rgba(51, 107, 135, 0.5)",
                  "0 10px 60px rgba(51, 107, 135, 0.3)",
                ],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <Icon size={96} color="#FFFFFF" strokeWidth={2.5} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
